import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Shield, MapPin, Clock, Star, ChevronLeft, Package, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedCurrency, setSelectedCurrency] = useState('BNB');
  const [prices, setPrices] = useState({});
  const [adding, setAdding] = useState(false);

  const CURRENCIES = ['BTC','ETH','BNB','SOL','USDT','SUI','ASTER','HYPE','XRP'];

  useEffect(() => {
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data.product);
      setSelectedCurrency(r.data.product.displayCurrency || 'BNB');
    }).catch(() => toast.error('Product not found')).finally(() => setLoading(false));
    api.get('/prices/live').then(r => setPrices(r.data.prices || {})).catch(() => {});
  }, [id]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="spinner" /></div>;
  if (!product) return <div className="text-center py-20 text-brand-muted">Product not found</div>;

  const color = COIN_COLORS[selectedCurrency] || '#FF6B00';
  const priceInCrypto = prices[selectedCurrency] ? (product.priceUSD * 1.015 / prices[selectedCurrency]) : product.displayPriceWithFee || 0;
  const decimals = selectedCurrency === 'BTC' ? 8 : selectedCurrency === 'ETH' ? 6 : 4;

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, qty, selectedCurrency);
    toast.success('Added to cart!');
  };

  const handleBuyNow = () => {
    if (!user) { navigate('/login'); return; }
    addToCart(product, qty, selectedCurrency);
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-6xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-1 text-brand-muted text-sm mb-6 hover:text-white transition-colors">
        <ChevronLeft size={16} /> Back to Shop
      </Link>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Images */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden mb-3" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <img src={product.images?.[imgIdx]?.url || 'https://via.placeholder.com/600/12121A/FF6B00?text=CM'}
              alt={product.title} className="w-full h-full object-cover" />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 transition-all"
                  style={{ border: `2px solid ${i === imgIdx ? '#FF6B00' : '#1E1E2E'}` }}>
                  <img src={img.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Sora' }}>{product.title}</h1>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} size={14} fill={i < Math.round(product.rating || 0) ? '#F5A623' : 'transparent'} stroke="#F5A623" />
              ))}
            </div>
            <span className="text-sm text-brand-muted">{product.rating?.toFixed(1) || '0.0'} ({product.reviewCount || 0} reviews)</span>
          </div>

          {/* Currency selector */}
          <div className="mb-4">
            <p className="text-xs text-brand-muted mb-2 font-medium">Pay with</p>
            <div className="flex flex-wrap gap-2">
              {CURRENCIES.map(c => (
                <button key={c} onClick={() => setSelectedCurrency(c)}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: selectedCurrency === c ? `${COIN_COLORS[c]}20` : '#12121A',
                    color: selectedCurrency === c ? COIN_COLORS[c] : '#6B6B8A',
                    border: `1px solid ${selectedCurrency === c ? COIN_COLORS[c] : '#1E1E2E'}`,
                    fontFamily: 'JetBrains Mono',
                  }}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Price breakdown */}
          <div className="p-4 rounded-xl mb-4" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-3xl font-bold" style={{ color, fontFamily: 'JetBrains Mono' }}>
                {(priceInCrypto * qty).toFixed(decimals)}
              </span>
              <span className="font-bold" style={{ color }}>{selectedCurrency}</span>
            </div>
            <p className="text-xs text-brand-muted">
              Product: ${(product.priceUSD * qty).toFixed(2)} + 1.5% network fee = ${(product.priceUSD * qty * 1.015).toFixed(2)} USD
            </p>
            <p className="text-xs mt-1" style={{ color: '#00C864' }}>
              ✓ Price locked in USD — crypto amount adjusts with market rate
            </p>
          </div>

          {/* Qty */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-brand-muted">Qty:</span>
            <div className="flex items-center gap-2" style={{ background: '#12121A', border: '1px solid #1E1E2E', borderRadius: 12, padding: '4px' }}>
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-7 h-7 rounded-lg flex items-center justify-center text-brand-muted hover:text-white transition-colors" style={{ background: '#1E1E2E' }}>−</button>
              <span className="w-8 text-center font-semibold text-sm">{qty}</span>
              <button onClick={() => setQty(q => Math.min(product.stock, q + 1))} className="w-7 h-7 rounded-lg flex items-center justify-center text-brand-muted hover:text-white transition-colors" style={{ background: '#1E1E2E' }}>+</button>
            </div>
            <span className="text-xs text-brand-muted">{product.stock} in stock</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 mb-3">
            <button onClick={handleAddToCart} className="btn-outline flex-1 flex items-center justify-center gap-2">
              <Plus size={15} /> Add to Cart
            </button>
            <button onClick={handleBuyNow} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <ShoppingCart size={15} /> Buy Now
            </button>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-2">
            {[
              [Shield, 'Escrow Safe'],
              [Package, 'Tracked Delivery'],
              [Clock, 'On-time Release'],
            ].map(([Icon, label]) => (
              <div key={label} className="flex flex-col items-center gap-1 p-2 rounded-xl text-center" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                <Icon size={16} className="text-brand-orange" />
                <span className="text-xs text-brand-muted">{label}</span>
              </div>
            ))}
          </div>

          {/* Shipping info */}
          <div className="mt-4 p-3 rounded-xl flex items-center gap-3 text-sm" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <MapPin size={16} className="text-brand-orange flex-shrink-0" />
            <div>
              <span className="text-brand-text font-medium">Ships from: </span>
              <span className="text-brand-muted">{product.shipsFrom?.city || ''} {product.shipsFrom?.country || 'Global'}</span>
            </div>
          </div>

          {/* Description */}
          <div className="mt-6">
            <h3 className="font-bold mb-2" style={{ fontFamily: 'Sora' }}>Description</h3>
            <p className="text-brand-muted text-sm leading-relaxed">{product.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
